import { Injectable } from '@angular/core';
import { CartModificationPayload, CompareCartData } from '@cart/const';
import { ActiveCartService, CartActions, CartApiService } from '@cart/data-access';
import { CityService } from '@city/feature';
import { EnvironmentService } from '@core/feature';
import { Actions, EffectNotification, createEffect, ofType } from '@ngrx/effects';
import { UserIdService } from '@spartacus/core';
import { EMPTY, Observable, combineLatest, iif, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';

@Injectable()
export class CompareCartEffects {
  loadDiff$: Observable<CartActions.LoadDiffFail | CartActions.LoadDiffSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LOAD_DIFF),
      map((action: CartActions.LoadDiff) => action.payload),
      mergeMap((newBaseSiteId: string) =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
          userId: this.userIdService.getUserId(),
        }).pipe(
          take(1),
          map(({ cartId, userId }) => ({ cartId, userId, newBaseSiteId, persistCart: false })),
        ),
      ),
      exhaustMap((data: CompareCartData) => {
        if (!data.cartId) {
          const diff: CartModificationPayload = { cartModifications: [], newBaseSiteId: data.newBaseSiteId };

          return of(new CartActions.LoadDiffSuccess(diff));
        } else {
          return this.occCartService.validateCart(data).pipe(
            map((diff: CartModificationPayload) => {
              diff.newBaseSiteId = data.newBaseSiteId;

              return new CartActions.LoadDiffSuccess(diff);
            }),
            catchError((error: unknown) => of(new CartActions.LoadDiffFail(error))),
          );
        }
      }),
    ),
  );

  validateCart$: Observable<CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.VALIDATE_CART),
      mergeMap(() =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
          userId: this.userIdService.getUserId(),
        }).pipe(
          take(1),
          filter(({ cartId, userId }) => !!cartId && !!userId),
        ),
      ),
      switchMap((data: CompareCartData) =>
        this.occCartService
          .validateCart(data)
          .pipe(map(() => new CartActions.LoadCart({ cartId: data.cartId, userId: data.userId }))),
      ),
    ),
  );

  confirmDiff$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.CONFIRM_DIFF),
        map((action: CartActions.ConfirmDiff) => action.payload),
        mergeMap((newBaseSiteId: string) =>
          combineLatest({
            cartId: this.activeCartService.getActiveCartId(),
            userId: this.userIdService.getUserId(),
          }).pipe(
            take(1),
            map(({ cartId, userId }) => ({ cartId, userId, newBaseSiteId, persistCart: true })),
          ),
        ),
        map((data: CompareCartData) => {
          this.occCartService.validateCart(data).subscribe(() => {
            this.cityService.setBaseStoreWithoutCompareCart(data.newBaseSiteId);
          });
        }),
      ),
    { dispatch: false },
  );

  loadDiffSuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.LOAD_DIFF_SUCCESS),
        map((action: CartActions.LoadDiffSuccess) => action.payload),
        map((data: CartModificationPayload) => {
          if (!data?.cartModifications || data.cartModifications.length === 0) {
            this.cityService.setBaseStoreWithoutCompareCart(data.newBaseSiteId);
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private activeCartService: ActiveCartService,
    private userIdService: UserIdService,
    private cityService: CityService,
    private occCartService: CartApiService,
    private environmentService: EnvironmentService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return iif(() => this.environmentService.isPlatformBrowser, resolvedEffects$, EMPTY);
  }
}
